import { useMutation } from "@tanstack/react-query";

import { queryClient } from "@/config/react-query";

import { toast } from "@/lib/use-toast";

import type { DocumentTypeConfig } from "@/types/document";

import {
  createDocumentType,
  deleteDocumentType,
  getAdminDocumentTypes,
  getDocumentTypes,
  updateDocumentType,
} from "./document";

export const documentTypeQueryKeys = {
  all: ["document-types"] as const,
  admin: ["admin", "document-types"] as const,
};

export const documentTypeQueryOptions = {
  list: () => ({
    queryKey: documentTypeQueryKeys.all,
    queryFn: () => getDocumentTypes(),
    select: (data: DocumentTypeConfig[] | null) => data ?? [],
  }),
  adminList: () => ({
    queryKey: documentTypeQueryKeys.admin,
    queryFn: () => getAdminDocumentTypes(),
    select: (data: DocumentTypeConfig[] | null) => data ?? [],
  }),
};

export const useCreateDocumentTypeMutation = () =>
  useMutation<DocumentTypeConfig | null, Error, string>({
    mutationFn: (name) => createDocumentType(name),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentTypeQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: documentTypeQueryKeys.admin,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to create document type",
        variant: "error",
      });
    },
  });

export const useUpdateDocumentTypeMutation = () =>
  useMutation<DocumentTypeConfig | null, Error, { id: string; name: string }>({
    mutationFn: ({ id, name }) => updateDocumentType(id, name),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentTypeQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: documentTypeQueryKeys.admin,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to update document type",
        variant: "error",
      });
    },
  });

export const useDeleteDocumentTypeMutation = () =>
  useMutation<void | null, Error, string>({
    mutationFn: (id) => deleteDocumentType(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: documentTypeQueryKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: documentTypeQueryKeys.admin,
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete document type",
        variant: "error",
      });
    },
  });
